import { chain, find, fromPairs, get, includes, map, memoize, some, toLower, filter, compact, isEmpty } from "lodash"
import { percentageFormatter, factorFormatter, makeCurrencyFormatter, CURRENCY_SCALES } from "helpers/formatters/numbers"
import { buildScaledCurrencyLabel } from "helpers/metrics/currency-scale-label"
import { createSelector } from "reselect"
import { BETA_FACTORS } from "helpers/analyzeConstants"
import { getMissingDataFiles } from "selectors/missingDataFiles"
import { getIsOpPro } from "modules/account-root/selectors"
import { makeComputeMetricFactorsCategories, mapMetricColumnCategories, makeRiskSortGetter, makeBetaSortGetter, makeContentSetSortGetter, makeDescriptorsSortGetter } from "helpers/securities-search"
import { getModelTopFactors, getModelFactors, getModelCategories } from "selectors/modelMetadata"
import { escapeContentSetId } from "helpers/contentSet"
import { getSelectedModelName } from "modules/market-tools/selectors"
import { getSupportedMetrics } from "modules/default-metric-units/selectors"

const EMPTY_ARRAY = []
const getForcedScaleFormatter = memoize(forcedScale => makeCurrencyFormatter(undefined, { forcedScale, valueOnly: true }))
const getRawModelId = (_, props) => get(props, "modelId")
const getRawSelectedColumns = (_, props) => get(props, "selectedColumns", null)
const getColumnSearches = (_, props) => get(props, "columnSearches", EMPTY_ARRAY)
const getSort = (_, props) => get(props, "sort", EMPTY_ARRAY)
const getHeaderCurrency = (_, props) => get(props, "currency")
const getContentSetsRaw = state => get(state, "global.data.contentSets.data.contentSets", EMPTY_ARRAY)

export const getContentSetsLoaded = createSelector(
  [state => get(state, "global.data.contentSets.status")],
  (status) => status === "ok",
)

export const getContentSets = createSelector(
  [getContentSetsRaw],
  (contentSets) => chain(contentSets)
    .sortBy("name")
    .map(contentSet => ({
      escapedId: escapeContentSetId(contentSet.id),
      ...contentSet,
    }))
    .value(),
)

function computeColumns(isOpPro) {
  const columns = [{
    category: "Descriptors",
    getter: (security, id) => get(security, id),
    sortGetter: makeDescriptorsSortGetter(),
    columns: [{
      type: "descriptors",
      id: "marketCapitalization",
      label: "Market Capitalization",
      shortLabel: "Mkt Cap",
      forcedScale: CURRENCY_SCALES.M,
    }, {
      type: "descriptors",
      id: "averageDailyVolume",
      label: "Average Daily Volume",
      shortLabel: "ADV",
      forcedScale: CURRENCY_SCALES.M,
    }],
  }]

  if (!isOpPro) {
    columns.push({
      category: "Risk",
      getter: (security, id) => {
        if (id === "total") {
          return get(security, "risk.standardDeviation.total")
        } else {
          return get(security, ["risk", "varianceDecomposition", "summary", id])
        }
      },
      sortGetter: makeRiskSortGetter(),
      columns: [{
        type: "risk",
        id: "total",
        label: "Total",
        formatter: value => percentageFormatter(value, true),
      }, {
        type: "risk",
        id: "factors",
        label: "Factor",
        formatter: value => percentageFormatter(value, true),
      }, {
        type: "risk",
        id: "specific",
        label: "Specific",
        formatter: value => percentageFormatter(value, true),
      }],
    })
  }
  return columns
}

export function computeSelectedColumns(selectedColumns, topFactors, missingDataFiles, modelId, isOpPro) {
  if (!isEmpty(selectedColumns)) {
    return selectedColumns
  }

  let result = {}

  if (!includes(missingDataFiles, "securityMarketcap")) {
    result["descriptors.marketCapitalization"] = true
  }

  if (!includes(missingDataFiles, "securityLiquidity")) {
    result["descriptors.averageDailyVolume"] = true
  }

  if (!isOpPro) {
    result = {
      ...result,
      "risk.total": true,
      "risk.factors": true,
      "risk.specific": true,
    }
  }

  return {
    ...result,
    ...fromPairs(map(topFactors, factor => [`exposure.${factor}`, true])),
  }
}

export const getSelectedColumns = createSelector(
  [getRawSelectedColumns, getModelTopFactors, getMissingDataFiles, getRawModelId, getIsOpPro],
  computeSelectedColumns,
)

const getExposure = createSelector(
  [getModelFactors, getModelCategories, getSelectedColumns, getSort, getSupportedMetrics],
  makeComputeMetricFactorsCategories("exposure"),
)

const getPredictedFactorSensitivity = createSelector(
  [getModelFactors, getModelCategories, getSelectedColumns, getSort, getSupportedMetrics],
  makeComputeMetricFactorsCategories("predictedFactorSensitivity"),
)

const getHistoricalFactorSensitivity = createSelector(
  [getModelFactors, getModelCategories, getSelectedColumns, getSort, getSupportedMetrics],
  makeComputeMetricFactorsCategories("historicalFactorSensitivity"),
)

const getContentSetsColumns = createSelector(
  [getContentSets, getSelectedColumns, getSort],
  (contentSets, selectedColumns, sort) => {
    return map(contentSets, ({ id: contentSetId, escapedId, name, factors, categories, proprietary }) => ({
      category: name,
      proprietary,
      sortGetter: makeContentSetSortGetter(contentSetId),
      getter: (security, id) => chain(security)
        .get(escapedId)
        .find(factor => factor.id === id)
        .get("zScore")
        .value(),
      columns: chain(categories)
        .map(category => {
          const result = {
            type: contentSetId,
            id: `${contentSetId}.${category.id}`,
            contentSetId,
            label: category.name,
            formatter: factorFormatter,
            items: chain(factors)
              .filter(factor => factor.category === category.id)
              .map(({ name, id }) => ({
                id,
                label: name,
                selected: !!selectedColumns[`${contentSetId}.${id}`],
                sorted: sort.id === id && sort.type === contentSetId ? toLower(sort.direction) : false,
              }))
              .value(),
          }

          result.partial = some(result.items, item => item.selected)
          result.sorted = get(find(result.items, item => item.sorted), "sorted", false)

          return result
        })
        .value(),
    }))
  })

const getBeta = createSelector(
  [getSelectedColumns, getSort, getSelectedModelName],
  (selectedColumns, sort, modelName) => {
    const items = ({
      category: "Beta",
      sortGetter: makeBetaSortGetter(),
      getter: (security, id) => {
        return get(security, `beta.${id}`, null)
      },
      formatter: value => value,
      columns: map(BETA_FACTORS, (factor, id) => {
        const result = {
          type: "beta",
          id,
          label: factor.label,
          shortLabel: factor.label,
          formatter: factorFormatter,
          selected: !!selectedColumns[`beta.${id}`],
          categoryTooltip: `${factor.label} Beta values are provided by the ${modelName} model.`,
          sorted: sort.id === id && sort.type === "beta" ? toLower(sort.direction) : false,
        }

        return result
      }),
    })
    return items
  },
)

const getColumns = createSelector(
  [getSelectedColumns, getSort, getIsOpPro],
  (selectedColumns, sort, isOpPro) => {
    const columns = computeColumns(isOpPro)
    return map(columns, category => ({
      ...category,
      columns: map(category.columns, column => ({
        ...column,
        selected: !!selectedColumns[`${column.type}.${column.id}`],
        sorted: sort.id === column.id && sort.type === column.type ? toLower(sort.direction) : false,
      })),
    }))
  },
)

export const getAvailableColumns = createSelector(
  [getColumns, getExposure, getContentSetsColumns, getBeta, getPredictedFactorSensitivity, getHistoricalFactorSensitivity, getIsOpPro],
  (columns, exposure, contentSetsColumns, beta, predictedFactorSensitivity, historicalFactorSensitivity, isOpPro) => compact([
    ...columns,
    exposure,
    ...contentSetsColumns,
    beta,
    !isOpPro && predictedFactorSensitivity,
    historicalFactorSensitivity,
  ]),
)

export const getTableColumns = createSelector(
  [getAvailableColumns, getHeaderCurrency],
  (availableColumns, currency) => chain(availableColumns)
    .map(category => ({
      ...category,
      columns: chain(category.columns)
        .map(column => {
          if (column.items) {
            return map(column.items, item => ({
              id: item.id,
              type: column.type,
              label: item.shortLabel || item.label,
              selected: item.selected,
              sorted: item.sorted,
              sortGetter: category.sortGetter,
              formatter: column.formatter,
              categoryTooltip: column.categoryTooltip,
            }))
          } else {
            const label = column.shortLabel || column.label
            const { forcedScale } = column
            return {
              id: column.id,
              type: column.type,
              label,
              ...(forcedScale && { headerLabel: buildScaledCurrencyLabel(label, forcedScale, currency) }),
              selected: column.selected,
              sorted: column.sorted,
              sortGetter: category.sortGetter,
              formatter: forcedScale ? getForcedScaleFormatter(forcedScale) : column.formatter,
              categoryTooltip: column.categoryTooltip,
            }
          }
        })
        .flatten()
        .filter(column => column.selected)
        .value(),
    }))
    .filter(category => !!category.columns.length)
    .value(),
)

export const getFilteredColumnsSelector = createSelector(
  [getColumns, getExposure, getColumnSearches, getContentSetsColumns, getBeta, getPredictedFactorSensitivity, getHistoricalFactorSensitivity, getIsOpPro],
  (columns, exposure, columnSearches, contentSetsColumns, beta, predictedFactorSensitivity, historicalFactorSensitivity, isOpPro) => compact([
    ...columns,
    mapMetricColumnCategories(exposure, columnSearches),
    !isOpPro && mapMetricColumnCategories(predictedFactorSensitivity, columnSearches),
    mapMetricColumnCategories(historicalFactorSensitivity, columnSearches),
    ...map(contentSetsColumns, column => ({
      ...column,
      columns: chain(column.columns)
        .map(category => {
          if (columnSearches[category.id]) {
            return ({
              ...category,
              items: chain(category.items)
                .filter(item => columnSearches[category.id] ? includes(toLower(item.label), toLower(columnSearches[category.id])) : item)
                .value(),
            })
          }

          return category
        })
        .value(),
    }),
    ),
    {
      ...beta,
      columns: map(beta.columns, category => {
        if (columnSearches[category.id]) {
          return ({
            ...category,
            items: filter(category.items, item => columnSearches[category.id] ? includes(toLower(item.label), toLower(columnSearches[category.id])) : item),
          })
        }

        return category
      }),
    },
  ]),
)
